/**
 * OCHAT: makes the reserved sections genuinely inaccessible, not just invisible.
 *
 * WHY THIS IS ENOUGH
 * Upstream already has the guard. dashboard/routes/index.js runs
 * validateLoggedInRoutes on every navigation, which ends in
 * routeIsAccessibleFor(to, userPermissions) and redirects to the dashboard when
 * the route's meta.permissions and the viewer's permissions do not intersect. So
 * nothing here has to intercept navigation: rewriting meta.permissions is the
 * whole mechanism. routeHelpers.js and permissionsHelper.js stay untouched.
 *
 * WHY A POLICY INSTEAD OF EDITING THE ROUTE FILES
 * The reserved sections span 25-odd routes in 13 files. Editing each one would put
 * the fork back into the position the sidebar was in before menuPolicy.js: a diff
 * spread over files upstream also edits every release. This is one call from
 * dashboard.routes.js.
 *
 * HOW THE REWRITE WORKS, AND WHY IT IS NOT A REPLACE
 * It removes 'administrator' and 'agent' and adds 'super_admin', keeping every
 * other permission the route declared. That matters: reports declares
 * ['administrator', 'report_manage'] and the Help Center declares
 * ['administrator', 'agent', 'knowledge_base_manage']. A blanket replace with
 * ['super_admin'] would strip the custom-role grants, and then
 * defaultRedirectPage() -- which sends a report_manage user to 'reports/overview'
 * and a knowledge_base_manage user to 'portals' -- would bounce them into a route
 * they can no longer enter. That is a redirect loop, not a denial.
 *
 * A route that inherits its permissions from a parent record gets an explicit
 * meta.permissions of its own, because Vue Router merges meta down the matched
 * chain and the child has to win.
 *
 * MUTATION, DELIBERATELY
 * Route records are cloned nowhere; only `route.meta` is reassigned, and to a NEW
 * object. That is not incidental: the route files declare one `const meta = {...}`
 * and share it by reference across every route in the file (see
 * settings/sla/sla.routes.js), so mutating meta in place would reach routes this
 * policy never matched. Reassigning the property leaves the shared object alone.
 *
 * The rewrite is idempotent: a second pass finds nothing to strip and the
 * permission already present.
 */
import { SUPER_ADMIN_PERMISSION } from '../config/permissions';
import { isReservedRoute } from '../config/reservedSections';

/**
 * The permissions that stand for "any member of this account". These are what the
 * reservation takes away; anything else the route declared is an explicit grant
 * and is left alone.
 */
const ACCOUNT_ROLE_PERMISSIONS = ['administrator', 'agent'];

const reservedPermissions = (declared = []) => [
  SUPER_ADMIN_PERMISSION,
  ...declared.filter(
    permission =>
      permission !== SUPER_ADMIN_PERMISSION &&
      !ACCOUNT_ROLE_PERMISSIONS.includes(permission)
  ),
];

/**
 * Rewrites meta.permissions in place on every reserved route, at any depth.
 *
 * @param {Array} routes a Vue Router route record array
 * @returns {Array} the same array, so it can be used inline
 */
export const applyOchatRoutePermissions = routes => {
  routes.forEach(route => {
    if (isReservedRoute(route.name)) {
      route.meta = {
        ...route.meta,
        permissions: reservedPermissions(route.meta?.permissions),
      };
    }

    if (route.children?.length) applyOchatRoutePermissions(route.children);
  });

  return routes;
};

export default applyOchatRoutePermissions;
